'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { useRef, useState } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import {
  CONTACT_SUBJECT_OPTIONS,
  type ContactPayload,
  type ContactSubject,
  contactSchema,
} from '@impacta/shared/schemas/forms';
import { FormError } from '@/components/forms/FormError';
import { FormSuccessCard } from '@/components/forms/FormSuccessCard';
import { SelectField } from '@/components/forms/SelectField';
import { SubmitButton } from '@/components/forms/SubmitButton';
import { TextAreaField } from '@/components/forms/TextAreaField';
import { TextField } from '@/components/forms/TextField';
import { TurnstileWidget, type TurnstileWidgetHandle } from '@/components/forms/TurnstileWidget';
import { errorMessage } from '@/lib/forms/error-messages';
import { submitForm } from '@/lib/forms/submit';

type Status = 'idle' | 'submitting' | 'success' | 'error';

type ContactFormProps = {
  subjectMode?: 'select' | 'hidden';
  defaultSubject?: ContactSubject;
  className?: string;
  successHeading?: string;
  successBody?: string;
  successLinkHref?: string;
  successLinkLabel?: string;
};

export default function ContactForm({
  subjectMode = 'select',
  defaultSubject = 'otro',
  className = 'bg-white rounded-2xl shadow-sm border border-[#E8E8E4] p-8 space-y-5',
  successHeading = '¡Mensaje enviado!',
  successBody = 'Te responderemos en un plazo de 24 horas hábiles.',
  successLinkHref = '/homepage',
  successLinkLabel = 'Volver al inicio',
}: ContactFormProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors, isValid, isSubmitting },
  } = useForm<ContactPayload>({
    mode: 'onChange',
    resolver: yupResolver(contactSchema) as unknown as Resolver<ContactPayload>,
    defaultValues: {
      name: '',
      email: '',
      message: '',
      ...(subjectMode === 'hidden' ? { subject: defaultSubject } : {}),
    },
  });

  if (status === 'success') {
    return (
      <FormSuccessCard
        heading={successHeading}
        body={successBody}
        linkHref={successLinkHref}
        linkLabel={successLinkLabel}
      />
    );
  }

  async function onValid(data: ContactPayload) {
    if (!token) return;
    setStatus('submitting');
    setErrorBanner(null);

    const result = await submitForm({
      endpoint: '/api/forms/contact',
      payload: data,
      token,
    });

    if (result.ok) {
      reset();
      setToken(null);
      setStatus('success');
    } else {
      setErrorBanner(errorMessage(result.errorCode, result.detail));
      turnstileRef.current?.reset();
      setStatus('error');
      console.error('submit-failed', { errorCode: result.errorCode });
    }
  }

  function onInvalid() {
    const first = Object.keys(errors)[0] as keyof ContactPayload | undefined;
    if (first) setFocus(first);
  }

  return (
    <form onSubmit={handleSubmit(onValid, onInvalid)} noValidate className={className}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <TextField
          name="name"
          label="Nombre completo"
          placeholder="Tu nombre"
          required
          register={register}
          error={errors.name?.message}
        />
        <TextField
          name="email"
          type="email"
          label="Correo electrónico"
          placeholder="tu@correo.cl"
          required
          register={register}
          error={errors.email?.message}
        />
      </div>

      {subjectMode === 'hidden' ? (
        <input type="hidden" {...register('subject')} />
      ) : (
        <SelectField
          name="subject"
          label="Asunto"
          options={CONTACT_SUBJECT_OPTIONS}
          placeholder="Selecciona un asunto"
          required
          register={register}
          error={errors.subject?.message}
        />
      )}

      <TextAreaField
        name="message"
        label="Mensaje"
        placeholder="Cuéntanos en qué podemos ayudarte..."
        required
        rows={5}
        register={register}
        error={errors.message?.message}
      />

      <TurnstileWidget ref={turnstileRef} onToken={setToken} />

      {errorBanner && <FormError variant="banner" message={errorBanner} />}

      <SubmitButton
        submitting={status === 'submitting'}
        disabled={!token || !isValid || status === 'submitting' || isSubmitting}
        idleLabel="Enviar mensaje"
        submittingLabel="Enviando..."
      />
    </form>
  );
}
